import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import GuestPreviewChatCard from '../components/GuestPreviewChatCard';
import { useAuth } from '../context/AuthContext';
import { trackJoinClick } from '../services/visitService';

const EnhancedHome = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [isChatOpen, setIsChatOpen] = useState(false);

  const isFullMode = process.env.REACT_APP_APP_MODE === 'full';

  useEffect(() => {
    if (isAuthenticated && isFullMode) {
      navigate('/chat');
    }
  }, [isAuthenticated, isFullMode, navigate]);

  const handleSignupClick = async () => {
    await trackJoinClick('Create free account (hero)', '/');
    navigate('/signup');
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,#fff1f5,transparent_35%),linear-gradient(180deg,#fffafc_0%,#ffffff_48%,#fff7fb_100%)] text-slate-900">
      <div className="mx-auto max-w-7xl px-3 pt-4 sm:px-6 lg:px-20">

        {/* Header */}
        <header className="flex items-center justify-center sm:justify-start gap-4 py-3 sm:py-5">
          <div className="flex items-center justify-center w-full sm:w-auto gap-3">
            {/* <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-rose-500 to-pink-500 text-sm font-bold text-white shadow-sm">
              B
            </div> */}
            <p className="font-['Dancing_Script'] text-3xl sm:text-2xl leading-none text-slate-900 text-center">
              The Bela Experience
            </p>
          </div>
          {/* <Link
            to="/login"
            className="inline-flex h-9 items-center justify-center rounded-full border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:border-rose-200 hover:bg-rose-50"
          >
            Log in
          </Link> */}
        </header>

        {/* Main hero */}
        <main className="pt-8 lg:pt-10 xl:pt-16 pb-8 xl:pb-20">
          <div className="grid items-center gap-6 md:gap-8 lg:grid-cols-2 lg:gap-16 xl:gap-24">

            {/* Left: copy + CTAs */}
            <div className="flex flex-col items-center space-y-8 text-center lg:items-start lg:space-y-8 xl:space-y-10 lg:text-left">
              <h1 className="font-playfair text-[2.75rem] font-bold leading-tight text-slate-900 sm:text-5xl lg:text-6xl xl:text-7xl">
                Create<br />your own<br />AI companion.
              </h1>

              {/* Pills */}
              <div className="flex flex-wrap justify-center gap-2 lg:justify-start">
                <span className="rounded-full border border-rose-200 bg-rose-50 px-3 py-1 lg:px-4 lg:py-1.5 text-xs lg:text-sm font-semibold text-slate-600">
                  Unfiltered Chat
                </span>
                <span className="rounded-full border border-rose-200 bg-rose-50 px-3 py-1 lg:px-4 lg:py-1.5 text-xs lg:text-sm font-semibold text-slate-600">
                  Uncensored Photos
                </span>
              </div>

              {/* CTAs */}
              <div className="flex flex-row items-center gap-3 xl:gap-5">
                <button
                  type="button"
                  onClick={handleSignupClick}
                  className="inline-flex h-12 xl:h-14 items-center justify-center rounded-xl bg-gradient-to-r from-rose-500 to-pink-500 px-6 sm:px-8 xl:px-10 text-sm xl:text-base font-semibold text-white shadow-[0_12px_30px_rgba(244,63,94,0.25)] transition hover:from-rose-600 hover:to-pink-600"
                >
                  <span className="sm:hidden">Create free account</span>
                  <span className="hidden sm:inline">Create your free account</span>
                </button>
                <Link
                  to="/login"
                  className="inline-flex h-12 xl:h-14 items-center justify-center rounded-xl border border-slate-200 bg-white px-6 xl:px-8 text-sm xl:text-base font-semibold text-slate-700 transition hover:border-rose-200 hover:bg-rose-50"
                >
                  Log in
                </Link>
              </div>

              <div className="flex flex-col gap-6 lg:items-start items-center w-full">
                <p className="text-xs xl:text-sm text-slate-400">18+ only · Adult content</p>

                {/* Inline chat preview trigger */}
                <button
                  onClick={() => setIsChatOpen(true)}
                  className="group flex w-full max-w-[360px] lg:max-w-md xl:max-w-lg items-center justify-start gap-4 xl:gap-5 rounded-2xl bg-white p-3 pr-5 xl:p-4 xl:pr-6 shadow-sm border border-slate-100 transition-all hover:shadow-md active:scale-95 mx-auto lg:mx-0"
                  aria-label="Open chat preview"
                >
                  <div className="relative shrink-0 flex h-12 w-12 xl:h-14 xl:w-14 items-center justify-center rounded-full bg-gradient-to-r from-rose-500 to-pink-500 text-white shadow-md">
                    {/* Ping animation effect */}
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-pink-400 opacity-60"></span>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 xl:h-7 xl:w-7 relative z-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                    </svg>
                  </div>
                  <div className="text-left w-full">
                    <span className="block text-sm xl:text-base font-bold text-slate-800 group-hover:text-rose-600 transition-colors">
                      Want a sneak peek?
                    </span>
                    <span className="block text-xs xl:text-sm text-slate-500 mt-0.5">
                      Click here and ask me anything
                    </span>
                  </div>
                </button>
              </div>
            </div>

            {/* Right: Hero Image */}
            <div className="flex justify-center xl:justify-end w-full">
              <img
                src="/images/belas.jpg"
                alt="Bela Preview"
                className="w-full max-w-[360px] sm:max-w-[400px] md:max-w-sm lg:max-w-lg xl:max-w-[600px] h-auto rounded-3xl shadow-xl"
              />
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="mt-16 flex flex-col items-center justify-between gap-4 border-t border-white/60 pt-8 text-sm text-slate-500 sm:flex-row">
          <span className="text-xs text-slate-400">© {new Date().getFullYear()} Bela. 18+ only.</span>
          <div className="flex items-center gap-4">
            <Link to="/terms" className="transition hover:text-rose-600">Terms of Use</Link>
            <Link to="/privacy" className="transition hover:text-rose-600">Privacy Policy</Link>
          </div>
        </footer>
      </div>

      {/* Overlay Modal for Chat */}
      {isChatOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-2 sm:p-4">
          <div className="relative w-full max-w-md animate-in fade-in zoom-in duration-200">
            <button
              onClick={() => setIsChatOpen(false)}
              className="absolute -top-12 right-0 md:-right-12 md:-top-0 flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/40 transition-colors"
              aria-label="Close chat preview"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <GuestPreviewChatCard
              onLoginRequest={() => navigate('/login')}
              onSignupClick={handleSignupClick}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedHome;
